// RefineCard.tsx — server refinement: live progress while the scan refines,
// the failure message when it failed, and Re-refine for ready/failed scans
// (POST /refine restarts bundle adjustment + photometric optimization), and
// Finish capture for a scan left in 'capturing' with ≥ 2 keyframes (the
// phone's Finish never reached the server), so it is never stranded.
// Next to Re-refine, the Max quality switch asks for the opt-in max-quality
// preset (refine_quality 'max': twice the training resolution, more splats
// and iterations; several times slower, GPU servers only). The switch starts
// from the scan's stored quality, and a max-quality refinement is marked
// with a Max quality badge in the header. Beside it, the Synthetic
// Enhancement switch (scan.synthetic_enhancement, stored the same way) adds
// training on Difix-cleaned renders of unseen viewpoints; a server without
// the Difix service answers 409, shown like any refine error. When /live
// reported that a live
// model (§15.7 live mode) was handed to refinement, `liveNote` says so under
// the progress.
//
// Refining costs compute (§6): it needs editor access and the
// openscan.compute scope (scan.can_compute). An editor without the scope gets
// an explanation and Enable compute, which signs in again asking for it; a
// viewer sees why there is no button. Compute run on someone else's scan is
// charged to the caller's own usage, and the card says so.
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { BsArrowClockwise, BsBoxArrowInRight, BsCheck2 } from 'react-icons/bs';
import { Badge } from 'common';
import { ApiError, api, atLeast, computeLoginUrl } from '../api';
import type { Scan } from '../api';
import { RefineProgress } from '../components/RefineProgress';
import { errorMessage } from '../format';
import type { ProgressMessage } from '../live';

// MAX_QUALITY_COST is what the switch costs, shown under it while it is on.
export const MAX_QUALITY_COST =
  'Trains at twice the resolution with more splats and iterations: several times slower ' +
  '(tens of minutes for a long scan) and more memory. Needs the GPU trainer.';

// SYNTHETIC_ENHANCEMENT_HELP is what the Synthetic Enhancement switch does,
// shown under it while it is on.
export const SYNTHETIC_ENHANCEMENT_HELP =
  'Renders the model from viewpoints the capture never saw, cleans those renders with NVIDIA Difix ' +
  '(research-only license) and trains on them too. Slower; not used with the lighting model.';

export interface RefineCardProps {
  scan: Scan;
  progress: ProgressMessage | null;
  onScan: (s: Scan) => void;
  onError: (msg: string) => void;
  // signedIn is false for a guest on a share link (changes the compute hint).
  signedIn?: boolean;
  // liveNote: e.g. "Refining from the live model" after a live-mode hand-off.
  liveNote?: string;
}

// currentPath is where Enable compute returns to (keeps ?share=).
function currentPath(): string {
  return window.location.pathname + window.location.search;
}

export function RefineCard({ scan, progress, onScan, onError, signedIn = true, liveNote }: RefineCardProps) {
  const [busy, setBusy] = useState(false);
  // refused is set when the server answered compute_scope_required even
  // though the scan said can_compute (a token minted before the grant).
  const [refused, setRefused] = useState(false);
  useEffect(() => setRefused(false), [scan.id]);
  const editor = atLeast(scan, 'editor');
  // A server without sharing sends no can_compute: it had no compute gate.
  const compute = editor && (scan.can_compute ?? true) && !refused;
  const foreign = scan.access !== undefined && scan.access !== 'owner';
  const storedMax = scan.refine_quality === 'max';
  const [maxQuality, setMaxQuality] = useState(storedMax);
  // Follow the stored quality when another scan is shown or it changes
  // underneath (another tab, a fallback to standard on the server).
  useEffect(() => setMaxQuality(storedMax), [scan.id, storedMax]);
  const storedSynthetic = scan.synthetic_enhancement === true;
  const [synthetic, setSynthetic] = useState(storedSynthetic);
  useEffect(() => setSynthetic(storedSynthetic), [scan.id, storedSynthetic]);
  const canRefine = scan.status === 'ready' || scan.status === 'failed';
  const canFinish = scan.status === 'capturing' && scan.keyframe_count >= 2;
  const showMaxBadge = storedMax && (scan.status === 'refining' || scan.status === 'ready');

  const refine = async () => {
    setBusy(true);
    try {
      const quality = maxQuality ? 'max' : 'standard';
      const s = canFinish ? await api.finishScan(scan.id) : await api.refineScan(scan.id, quality, synthetic);
      console.info(
        `[openscan/viewer] ${canFinish ? 'finish' : `re-refine (${quality}${synthetic ? ', synthetic enhancement' : ''})`} started for ${scan.id}`,
      );
      onScan(s);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'compute_scope_required') setRefused(true);
      else onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex align-items-center gap-2">
        Refinement
        {showMaxBadge && (
          <Badge tone="info" data-testid="max-quality-badge">
            Max quality
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        {scan.status === 'refining' && <RefineProgress progress={progress} fallback={scan.refine} />}
        {liveNote && (scan.status === 'refining' || scan.status === 'capturing') && (
          <div className="small text-body-secondary mt-1" data-testid="live-note">
            {liveNote}
          </div>
        )}
        {scan.status === 'ready' && (
          <span className="small">
            Refined{storedMax ? ' at max quality' : ''}
            {scan.refine?.psnr ? ` to ${scan.refine.psnr.toFixed(1)} dB PSNR` : ''} over {scan.keyframe_count} keyframes.
          </span>
        )}
        {scan.status === 'failed' && <span className="small text-danger">{scan.error || 'Refinement failed.'}</span>}
        {scan.status === 'capturing' && (
          <span className="small text-body-secondary">
            Still capturing on the phone.{canFinish ? ' If the phone stopped without finishing, finish it here.' : ''}
          </span>
        )}
      </Card.Body>
      <Card.Footer>
        {!editor ? (
          <span className="small text-body-secondary">You have view access; only the owner and editors can refine this scan.</span>
        ) : !compute ? (
          <div className="d-flex flex-wrap align-items-center gap-2" data-testid="compute-required">
            <span className="small text-body-secondary me-auto">
              {signedIn
                ? 'Refining uses server compute, which needs the openscan.compute permission.'
                : 'Refining uses server compute. Sign in with an account that has the openscan.compute permission.'}
              {foreign ? ' Runs you start here count against your own usage.' : ''}
            </span>
            <Button size="sm" variant="outline-primary" as="a" href={computeLoginUrl(currentPath())}>
              <BsBoxArrowInRight className="me-1" />
              Enable compute
            </Button>
          </div>
        ) : canFinish ? (
          <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => void refine()}>
            <BsCheck2 className="me-1" />
            Finish capture
          </Button>
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Button size="sm" variant="outline-primary" disabled={!canRefine || busy} onClick={() => void refine()}>
                <BsArrowClockwise className="me-1" />
                Re-refine
              </Button>
              {canRefine && (
                <Form.Check
                  type="switch"
                  id="refine-max-quality"
                  label="Max quality (slower)"
                  checked={maxQuality}
                  disabled={busy}
                  onChange={(e) => setMaxQuality(e.target.checked)}
                  title={MAX_QUALITY_COST}
                />
              )}
              {canRefine && (
                <Form.Check
                  type="switch"
                  id="refine-synthetic-enhancement"
                  label="Synthetic Enhancement"
                  checked={synthetic}
                  disabled={busy}
                  onChange={(e) => setSynthetic(e.target.checked)}
                  title={SYNTHETIC_ENHANCEMENT_HELP}
                />
              )}
            </div>
            {canRefine && maxQuality && <div className="small text-body-secondary mt-1">{MAX_QUALITY_COST}</div>}
            {canRefine && synthetic && (
              <div className="small text-body-secondary mt-1">{SYNTHETIC_ENHANCEMENT_HELP}</div>
            )}
          </>
        )}
        {editor && compute && foreign && (canRefine || canFinish) && (
          <div className="small text-body-secondary mt-1">This is {scan.owner}'s scan: refinements you start are charged to your usage.</div>
        )}
      </Card.Footer>
    </Card>
  );
}
